import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import NavBar from './components/NavBar';
import CookieBanner from './components/CookieBanner';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import CareersPage from './pages/CareersPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PublicationDetailPage from './pages/PublicationDetailPage';
import StatusPage from './pages/StatusPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import Footer from './components/Footer';
import './App.css';

const ScrollManager = () => {
  const location = useLocation();

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const scrollToTarget = () => {
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
        }
      };
      const timeout = setTimeout(scrollToTarget, 0);
      return () => clearTimeout(timeout);
    }

    window.scrollTo({ top: 0, left: 0, behavior: scrollBehavior });

    const main = document.getElementById('main');
    if (main) {
      main.focus({ preventScroll: true });
    }
  }, [location.pathname, location.hash]);

  return null;
};

const AppContent = () => {
  const location = useLocation();
  const hideChrome = location.pathname === '/login';

  return (
    <div className={`app-shell ${hideChrome ? 'app-shell--login' : ''}`}>
      <div className="cursor-glow" aria-hidden="true" />
      <a className="skip-link" href="#main">
        Skip to main content
      </a>
      {!hideChrome ? <NavBar /> : null}

      <main id="main" className={`app-main ${hideChrome ? 'app-main--login' : ''}`} tabIndex={-1}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/publications/:slug" element={<PublicationDetailPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      {!hideChrome ? <Footer /> : null}
      <CookieBanner />
    </div>
  );
};

function App() {
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;

    if (prefersReducedMotion || !hasFinePointer) {
      return undefined;
    }

    const root = document.documentElement;
    document.body.classList.add('has-cursor-glow');

    let rafId = null;
    let latestPosition = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

    const updatePosition = () => {
      root.style.setProperty('--cursor-x', `${latestPosition.x}px`);
      root.style.setProperty('--cursor-y', `${latestPosition.y}px`);
      root.style.setProperty('--cursor-opacity', '1');
      rafId = null;
    };

    const handleMove = (event) => {
      latestPosition = { x: event.clientX, y: event.clientY };
      if (rafId === null) {
        rafId = window.requestAnimationFrame(updatePosition);
      }
    };

    const handleLeave = () => {
      root.style.setProperty('--cursor-opacity', '0');
    };

    window.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseleave', handleLeave);
    document.addEventListener('mouseenter', updatePosition);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseleave', handleLeave);
      document.removeEventListener('mouseenter', updatePosition);
      document.body.classList.remove('has-cursor-glow');
      root.style.removeProperty('--cursor-x');
      root.style.removeProperty('--cursor-y');
      root.style.removeProperty('--cursor-opacity');
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
